/**
 *
 * TextEditor
 *
 */

import React from 'react'

// NPM Libraries
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Editor } from 'react-draft-wysiwyg'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { EditorState, ContentState, convertToRaw, convertFromRaw, Modifier } from 'draft-js'
import { withState } from 'recompose'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import styled from '@emotion/styled'
import DropDownMenu from 'components/DropDownMenu'

export const defaultWrapperStyle = {
	position: 'relative',
	// backgroundColor: '#FFF',
	border: '1px solid #AAA',
}

export const defaultEditorStyle = {
	padding: '0 2em',
	height: 'calc(100% - 44px)',
	// backgroundColor: '#FFF',
}

export const defaultToolbarStyle = {
	width: '100%',
	backgroundColor: '#FFF',
	zIndex: 2,
}

const CustomButton = styled.button`
	padding: 0 10px;
	height: 23px;
	border: 1px solid #f1f1f1;

	&:hover {
		box-shadow: 1px 1px 0px #bfbdbd;
	}
`

const SaveButton = ({ save, has_changes }) => (
	<button
		type="button"
		className={classNames('btn', 'btn-sm', 'ml-2', {
			'btn-outline-success': !has_changes,
			'btn-success': has_changes,
		})}
		onClick={save}
	>
		<FontAwesomeIcon icon="save" />
	</button>
)

const MergeTagsButton = ({ merge_tag_options = [], editorState, onChange }) => {
	if (merge_tag_options.length === 0) return null

	const handleAddMergeTag = (value) => {
		const contentState = Modifier.replaceText(
			editorState.getCurrentContent(),
			editorState.getSelection(),
			`${value} `,
			editorState.getCurrentInlineStyle(),
		)

		onChange(EditorState.push(editorState, contentState, 'insert-characters'))
	}

	return (
		<DropDownMenu
			Button={<CustomButton className="btn btn-sm ml-2">Merge Tags</CustomButton>}
			button_style={{ textDecoration: 'none' }}
		>
			<ul className="list-unstyled text-center" style={{ fontSize: '0.9rem' }}>
				{merge_tag_options.map((option) => (
					<li key={option.value} onClick={() => handleAddMergeTag(option.value)} role="presentation">
						{option.text}
					</li>
				))}
			</ul>
		</DropDownMenu>
	)
}

function TextEditor({
	wrapperStyle,
	editorStyle,
	toolbarStyle,
	toolbarCustomButtons,
	editorState,
	setEditorState,
	defaultContentState,
	has_changes,
	onSave,
	editorDidChange,
	disabled,
	merge_tag_options = [],
	...props
}) {
	const save = () => {
		onSave && onSave(convertToRaw(editorState.getCurrentContent()))
	}

	const defaultToolbarCustomButtons = [
		<MergeTagsButton merge_tag_options={merge_tag_options} />,
		<SaveButton save={save} has_changes={has_changes} />,
	]

	const onEditorStateChange = (newEditorState) => {
		if (editorState.getCurrentContent() !== newEditorState.getCurrentContent()) {
			editorDidChange && editorDidChange()
		}

		setEditorState(newEditorState)
	}

	return (
		<Editor
			defaultEditorState={editorState}
			onEditorStateChange={onEditorStateChange}
			wrapperStyle={{ ...defaultWrapperStyle, ...wrapperStyle }}
			editorStyle={{ ...defaultEditorStyle, ...editorStyle }}
			toolbarStyle={{ ...defaultToolbarStyle, ...toolbarStyle }}
			toolbarCustomButtons={[...toolbarCustomButtons, ...defaultToolbarCustomButtons]}
			toolbarHidden={disabled} // Hides the entire toolbar
			{...props}
		/>
	)
}

TextEditor.propTypes = {
	wrapperStyle: PropTypes.object,
	editorStyle: PropTypes.object,
	toolbarStyle: PropTypes.object,
	toolbarCustomButtons: PropTypes.arrayOf(PropTypes.any),
	onSave: PropTypes.func,
	editorDidChange: PropTypes.func,
}

TextEditor.defaultProps = {
	wrapperStyle: {},
	editorStyle: {},
	toolbarStyle: {},
	toolbarCustomButtons: [],
}

export default withState('editorState', 'setEditorState', ({ default_content }) => {
	if (default_content === null || !Object.keys(default_content).length) default_content = ''

	let content_state
	switch (typeof default_content) {
		case 'undefined':
			content_state = ContentState.createFromText('')
			break
		case 'string':
			content_state = ContentState.createFromText(default_content)
			break
		default:
			content_state = convertFromRaw(default_content)
			break
	}

	return EditorState.createWithContent(content_state)
})(TextEditor)
